import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Divider,
	FormControl,
	Grid,
	Icon,
	IconButton,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { IProvisionListBySinister, ProvisionService } from "../../../../shared/services/api/provisoes/ProvisionService";

interface Props {
	open: boolean;
	row: IProvisionListBySinister | null;
	onClose: () => void;
	onSuccess: () => void;
}

// Editar dados bancários de um borderô já criado (item 4) — endpoint ainda não existe no backend,
// ver GAPS_BORDERO_PARA_BACKEND.md.
export const ModalEditBorderoBankData = ({ open, row, onClose, onSuccess }: Props) => {
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(false);

	const [bankCode, setBankCode] = useState("");
	const [agencyNumber, setAgencyNumber] = useState("");
	const [agencyDigit, setAgencyDigit] = useState("");
	const [accountNumber, setAccountNumber] = useState("");
	const [accountDigit, setAccountDigit] = useState("");
	const [accountType, setAccountType] = useState<"corrente" | "poupanca">("corrente");
	const [pixKey, setPixKey] = useState("");
	const [pixKeyType, setPixKeyType] = useState("");

	useEffect(() => {
		if (!open || !row) return;
		setBankCode(row.bankCode ?? "");
		setAgencyNumber(row.agencyNumber ?? "");
		setAgencyDigit(row.agencyDigit ?? "");
		setAccountNumber(row.accountNumber ?? "");
		setAccountDigit(row.accountDigit ?? "");
		setAccountType("corrente");
		setPixKey(row.pixKey ?? "");
		setPixKeyType(row.pixKeytYpe ?? "");
	}, [open, row]);

	const handleConfirm = async () => {
		if (!row) return;
		setIsLoading(true);
		const result = await ProvisionService.borderoUpdateBankData(row.id, {
			bankCode: bankCode || undefined,
			agencyNumber: agencyNumber || undefined,
			agencyDigit: agencyDigit || undefined,
			accountNumber: accountNumber || undefined,
			accountDigit: accountDigit || undefined,
			accountType,
			pixKey: pixKey || undefined,
			pixKeyType: pixKeyType || undefined,
		});
		setIsLoading(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", position: "top-right", messageOrContent: <ToastContent title="Erro" content={result.message} /> });
			return;
		}

		toastHandler.present({
			type: "success",
			position: "top-right",
			messageOrContent: <ToastContent title="Sucesso" content="Dados bancários atualizados." />,
		});
		onSuccess();
		onClose();
	};

	return (
		<Modal open={open} onClose={onClose}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "background.paper",
					boxShadow: 24,
					p: 4,
					width: "95%",
					maxWidth: 640,
					borderRadius: 3,
				}}
			>
				<LoadingComp isopen={isLoading} />

				<Grid container justifyContent="space-between" alignItems="center">
					<Typography variant="h5" sx={{ color: "primary.main", fontWeight: 300 }}>
						Editar Dados Bancários
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Grid>

				<Divider sx={{ my: 2 }} />

				<Grid container spacing={2}>
					<Grid item xs={12} sm={4}>
						<TextField fullWidth size="small" label="Banco" value={bankCode} onChange={(e) => setBankCode(e.target.value)} />
					</Grid>
					<Grid item xs={6} sm={3}>
						<TextField fullWidth size="small" label="Agência" value={agencyNumber} onChange={(e) => setAgencyNumber(e.target.value)} />
					</Grid>
					<Grid item xs={6} sm={2}>
						<TextField fullWidth size="small" label="Dígito" value={agencyDigit} onChange={(e) => setAgencyDigit(e.target.value)} />
					</Grid>
					<Grid item xs={12} sm={3}>
						<FormControl fullWidth size="small">
							<InputLabel id="edit-bordero-account-type-label">Tipo</InputLabel>
							<Select
								labelId="edit-bordero-account-type-label"
								label="Tipo"
								value={accountType}
								onChange={(e: SelectChangeEvent) => setAccountType(e.target.value as "corrente" | "poupanca")}
							>
								<MenuItem value="corrente">Conta Corrente</MenuItem>
								<MenuItem value="poupanca">Poupança</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={6} sm={4}>
						<TextField fullWidth size="small" label="Número da conta" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
					</Grid>
					<Grid item xs={6} sm={2}>
						<TextField fullWidth size="small" label="Dígito" value={accountDigit} onChange={(e) => setAccountDigit(e.target.value)} />
					</Grid>
					<Grid item xs={12} sm={3}>
						<TextField fullWidth size="small" label="Tipo de chave Pix" value={pixKeyType} onChange={(e) => setPixKeyType(e.target.value)} />
					</Grid>
					<Grid item xs={12} sm={3}>
						<TextField fullWidth size="small" label="Chave Pix" value={pixKey} onChange={(e) => setPixKey(e.target.value)} />
					</Grid>
				</Grid>

				<Grid container justifyContent="flex-end" flexDirection={smDown ? "column" : "row"} gap={2} mt={3}>
					<Button color="inherit" variant="contained" startIcon={<Icon>arrow_back</Icon>} onClick={onClose} sx={{ minWidth: 140 }}>
						Voltar
					</Button>
					<Button color="primary" variant="contained" startIcon={<Icon>check</Icon>} onClick={handleConfirm} sx={{ minWidth: 140 }}>
						Salvar
					</Button>
				</Grid>
			</Box>
		</Modal>
	);
};
